import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import type { ComponentProps } from 'react';
import { cn } from '../../common/functions/cn';
import { useTopLayerPortal } from '../../common/hooks/use-top-layer-portal';
import { preventDialogCloseOnEscape } from '../common-functions';

const Popover = ({ onOpenChange, ...props }: PopoverPrimitive.Root.Props) => {
  return (
    <PopoverPrimitive.Root
      data-slot="popover"
      onOpenChange={(open, details) => {
        preventDialogCloseOnEscape(open, details);
        onOpenChange?.(open, details);
      }}
      {...props}
    />
  );
};

const PopoverTrigger = ({ className, ...props }: PopoverPrimitive.Trigger.Props) => {
  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      className={cn('outline-none focus-visible:ring-2 focus-visible:ring-ring/50', className)}
      {...props}
    />
  );
};

const PopoverClose = ({ ...props }: PopoverPrimitive.Close.Props) => {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
};

type PopoverAlign = PopoverPrimitive.Positioner.Props['align'];
type PopoverSide = PopoverPrimitive.Positioner.Props['side'];

const PopoverContent = ({
  className,
  align = 'center',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  collisionAvoidance,
  anchor,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset' | 'collisionAvoidance' | 'anchor'
  >) => {
  const { portalAnchor, portalProps, positionerProps } = useTopLayerPortal();

  return (
    <>
      {portalAnchor}
      <PopoverPrimitive.Portal {...portalProps}>
        <PopoverPrimitive.Positioner
          {...positionerProps}
          align={align}
          alignOffset={alignOffset}
          side={side}
          sideOffset={sideOffset}
          collisionAvoidance={collisionAvoidance}
          anchor={anchor}
          className="isolate z-50"
        >
          <PopoverPrimitive.Popup
            data-slot="popover-content"
            className={cn(
              'z-50 flex w-72 flex-col gap-2.5 rounded-md bg-popover p-2.5 text-popover-foreground text-sm shadow-md outline-hidden ring-1 ring-border duration-100',
              className
            )}
            {...props}
          />
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </>
  );
};

const PopoverHeader = ({ className, ...props }: ComponentProps<'div'>) => {
  return <div data-slot="popover-header" className={cn('flex flex-col gap-1 text-sm', className)} {...props} />;
};

const PopoverTitle = ({ className, ...props }: PopoverPrimitive.Title.Props) => {
  return (
    <PopoverPrimitive.Title data-slot="popover-title" className={cn('font-medium text-base', className)} {...props} />
  );
};

const PopoverDescription = ({ className, ...props }: PopoverPrimitive.Description.Props) => {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={cn('text-muted-foreground', className)}
      {...props}
    />
  );
};

export {
  Popover,
  type PopoverAlign,
  PopoverClose,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  type PopoverSide,
  PopoverTitle,
  PopoverTrigger,
};
